import '@react-awesome-query-builder/mui/css/styles.css';
import './css/index.scss';
import React from 'react';
import clsx from 'clsx';
import { Alert, Theme, Typography } from '@mui/material';
import { Builder, Query } from '@react-awesome-query-builder/ui';
import { Config, ImmutableTree, Utils } from '@react-awesome-query-builder/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colorPalette } from '@qualifio/ui-commons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { makeStyles } from '@mui/styles';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const { jsonLogicFormat } = Utils;

const styles = ({ spacing }: Theme) => {
  return {
    root: {
      marginTop: spacing(2),
    },
    title: {
      fontWeight: 'bold',
    },
    builderContainer: {
      padding: spacing(1),
    },
    queryBuilder: {},
    errorIcon: {
      marginLeft: spacing(0.5),
    },
    criteriaDescription: {
      fontSize: '14px',
      fontWeight: 400,
      marginTop: spacing(2),
      '& a': {
        textDecoration: 'none !important',
        color: colorPalette.blue.main,
      },
    },
  };
};

const useStyles = makeStyles(styles);

interface Props {
  error?: string;
  tree: ImmutableTree;
  onChange: (tree: ImmutableTree, conf: Config, newVal: Record<string, unknown>) => void;
  config: Config;
  label?: string;
  description?: string | React.ReactNode;
}

export function RulesQueryBuilder({
  tree,
  onChange,
  error,
  config,
  label,
  description,
}: Props): React.ReactElement {
  const { t } = useTranslation();
  const classes = useStyles();
  const { getValues } = useFormContext();

  return (
    <div className={classes.root}>
      <Typography variant="h3" className={classes.title}>
        {label ?? t('earning-rules.properties.criteria')}
        {error && (
          <FontAwesomeIcon className={classes.errorIcon} color="red" icon={faExclamationCircle} />
        )}
      </Typography>
      {description && <div className={classes.criteriaDescription}>{description}</div>}
      {error && <Alert severity="error">{error}</Alert>}
      <Query
        {...config}
        value={tree}
        onChange={(tree: ImmutableTree, conf: Config) => {
          const newVal = jsonLogicFormat(tree, conf).logic as Record<string, unknown>;
          if (!newVal || JSON.stringify(newVal) !== JSON.stringify(getValues()?.criteria)) {
            onChange(tree, conf, newVal);
          }
        }}
        renderBuilder={builderProps => (
          <div className={clsx(classes.builderContainer, 'query-builder-container')}>
            <div className={clsx(classes.queryBuilder, 'query-builder')}>
              <Builder {...builderProps} />
            </div>
          </div>
        )}
      />
    </div>
  );
}
